import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import "chart.js/auto";
import Parse from "parse";
import { useEffect, useState } from "react";
import { Line } from "react-chartjs-2";
import { useTranslation } from "react-i18next";
import { useCase, useCaseSubmit } from "../../context/CaseContext";
import { useCurrency } from "../../context/CurrencyContext";
import { useTheme } from "../../context/ThemeContext";
import { Stage } from "../../shared/CaseConfiguration";
import { BookedReserve } from "../../shared/CaseModel";
import { percentage } from "../../shared/percentage";
import { reserveTotal } from "../../shared/reserve";
import { ReserveSnapshot, SnapshotEvent } from "../../shared/reserveHistory";
import { getTicks, getTooltip } from "../../utils/chartjsTheming";
import { Metric } from "../general/DataAtoms";
import { TABULAR_NUMERALS } from "../general/dataAtoms/shared";
import { SectionLabel } from "../general/SectionLabel";
import { showDate } from "../timeline/TimelinePage";
import { BookReserveDialog } from "./BookReserveDialog";

const card = { borderRadius: 3 } as const;
const amount = { ...TABULAR_NUMERALS, whiteSpace: "nowrap" } as const;

/** The reserve history of a case, oldest first, as its user may read it. */
export const fetchReserveHistory = (
  caseId: string,
): Promise<ReserveSnapshot[]> =>
  Parse.Cloud.run("getReserveHistory", { caseId });

/**
 * The reserve history of the open case, as the server recorded it; asked for
 * anew whenever the case is saved. Undefined while it is on its way.
 */
export function useReserveHistory(): ReserveSnapshot[] | undefined {
  const c = useCase().getCase();
  const [history, setHistory] = useState<{
    of: string;
    snapshots: ReserveSnapshot[];
  }>();
  const key = `${c.id}@${c.updatedAt}`;
  useEffect(() => {
    let cancelled = false;
    fetchReserveHistory(c.id)
      .then((snapshots) => {
        if (!cancelled) setHistory({ of: key, snapshots });
      })
      .catch((error) => {
        console.warn("Could not load the reserve history", error);
        if (!cancelled) setHistory({ of: key, snapshots: [] });
      });
    return () => {
      cancelled = true;
    };
  }, [c.id, key]);
  return history?.of === key ? history.snapshots : undefined;
}

/**
 * How a party's indicated reserve moved: recorded at each stage closed and
 * settlement accepted, and once a day after other changes.
 */
export const ReserveHistory = ({
  partyId,
  history,
  indicated,
}: {
  partyId: string;
  history: ReserveSnapshot[] | undefined;
  // the reserve the outcomes indicate now
  indicated: number;
}) => {
  const { t } = useTranslation(undefined, { keyPrefix: "reserve.history" });
  const { t: tRoot } = useTranslation();
  const { theme } = useTheme();
  const { currency } = useCurrency();
  const caseContext = useCase();
  const c = caseContext.getCase();
  const [booking, setBooking] = useState(false);
  const submit = useCaseSubmit();
  const submitBooking = (bookedReserves: BookedReserve[]) =>
    submit(
      { bookedReserves: JSON.stringify(bookedReserves) },
      { method: "put" },
    );
  const bookings = (c.bookedReserves ?? []).filter(
    (booked) => booked.partyId === partyId,
  );
  const booked = latestBooking(bookings);

  const snapshots = (history ?? []).filter((s) => s.partyId === partyId);
  const totals = snapshots.map(({ total }) => reserveTotal(total));
  const stageName = (stage: Stage) => tRoot(`home.stages.${Stage[stage]}`);
  const partyName = (id: string) =>
    c.parties?.find((party) => party.id === id)?.name ?? id;

  const describe = (event: SnapshotEvent): string => {
    switch (event.kind) {
      case "first":
        return t("event.first");
      case "stageClosed":
        return t("event.stageClosed", { stage: stageName(event.stage) });
      case "judgment":
        return t("event.judgment");
      case "judgmentEdited":
        return t("event.judgmentEdited");
      case "caseClosed":
        return t("event.caseClosed");
      case "settlementAccepted": {
        const offer = c.settlements?.find(({ id }) => id === event.offerId);
        return offer
          ? t("event.settlementAccepted", {
              offeror: partyName(offer.offerorId),
              offeree: partyName(offer.offereeId),
            })
          : t("event.settlementAcceptedUnknown");
      }
      case "reserveBooked":
        return t(event.reason ? "event.bookedFor" : "event.booked", {
          amount: currency.format(event.amount),
          date: showDate(event.bookedOn),
          reason: event.reason,
        });
      case "changed":
        return t("event.changed", {
          fields: event.fields.map((field) => t(`field.${field}`)).join(", "),
        });
    }
  };

  const header = (
    <Box>
      <Typography
        variant="h6"
        component="h2"
        sx={{ fontWeight: 700, letterSpacing: "-.005em" }}
      >
        {t("title")}
      </Typography>
      <Typography
        variant="body2"
        sx={{ color: "text.secondary", mt: 0.5, maxWidth: "76ch" }}
      >
        {t("intro")}
      </Typography>
    </Box>
  );

  // the reserve booked in the insurer's books, set against the indicated
  const bookedCard = (
    <Paper variant="outlined" sx={{ ...card, px: 2.5, pt: 2, pb: 2.25 }}>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1.5,
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <SectionLabel component="h3">{t("booked.title")}</SectionLabel>
        {!caseContext.readOnly && (
          <Button
            size="small"
            variant="outlined"
            sx={{ textTransform: "none", fontWeight: 600 }}
            onClick={() => setBooking(true)}
          >
            {t("booked.book")}
          </Button>
        )}
      </Box>
      {booked ? (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: 2,
            mt: 1.75,
          }}
        >
          <Metric
            label={t("booked.booked")}
            value={booked.amount}
            caption={[
              t("booked.on", { date: showDate(booked.bookedOn) }),
              booked.by
                ? t(booked.imported ? "booked.importedBy" : "booked.by", {
                    name: booked.by.name,
                  })
                : booked.imported && t("booked.imported"),
              booked.reason,
            ]
              .filter(Boolean)
              .join(" · ")}
          />
          <Metric label={t("booked.indicated")} value={indicated} />
          <Metric
            label={t("booked.difference")}
            value={booked.amount - indicated}
            caption={
              indicated !== 0
                ? t(
                    booked.amount >= indicated
                      ? "booked.above"
                      : "booked.below",
                    {
                      share: percentage.format(
                        Math.abs(booked.amount - indicated) /
                          Math.abs(indicated),
                      ),
                    },
                  )
                : undefined
            }
          />
        </Box>
      ) : (
        <Typography variant="body2" sx={{ mt: 1.75, color: "text.secondary" }}>
          {t("booked.none")}
        </Typography>
      )}
      {booking && (
        <BookReserveDialog
          indicated={indicated}
          onClose={(entered) => {
            setBooking(false);
            if (entered)
              submitBooking([
                ...(c.bookedReserves ?? []),
                { ...entered, partyId },
              ]);
          }}
        />
      )}
    </Paper>
  );

  if (history === undefined)
    return (
      <>
        {header}
        {bookedCard}
      </>
    );
  if (snapshots.length === 0)
    return (
      <>
        {header}
        {bookedCard}
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {t("none")}
        </Typography>
      </>
    );

  const current = totals[totals.length - 1];
  const previous = totals.length > 1 ? totals[totals.length - 2] : undefined;
  const last = snapshots[snapshots.length - 1];

  return (
    <>
      {header}
      {bookedCard}
      <Paper variant="outlined" sx={{ ...card, px: 2.5, pt: 2, pb: 2.25 }}>
        <SectionLabel component="h3">{t("indicated")}</SectionLabel>
        <Metric
          label={t("latest", { date: showDate(last.day) })}
          value={current}
          caption={
            previous === undefined
              ? t("onlyOne")
              : t("since", {
                  movement: signed(current - previous, currency.format),
                  date: showDate(snapshots[snapshots.length - 2].day),
                })
          }
          sx={{ mt: 1.75 }}
        />
        {snapshots.length > 1 && (
          <Box sx={{ height: 240, mt: 2 }}>
            <Line
              aria-label={t("chartLabel")}
              data={{
                datasets: [
                  {
                    label: t("indicated"),
                    // by its place among the records: several can be of one day
                    data: totals.map((y, x) => ({ x, y })),
                    stepped: "before",
                    borderColor: theme.palette.primary.main,
                    backgroundColor: theme.palette.primary.main,
                    pointRadius: 3,
                  },
                  ...(bookings.length > 0
                    ? [
                        {
                          label: t("booked.booked"),
                          // the reserve booked by the day of each record
                          data: snapshots.map(({ day }, x) => ({
                            x,
                            y:
                              latestBooking(
                                bookings.filter(
                                  ({ bookedOn }) => bookedOn <= day,
                                ),
                              )?.amount ?? null,
                          })),
                          stepped: "before" as const,
                          borderColor: theme.palette.warning.main,
                          backgroundColor: theme.palette.warning.main,
                          borderDash: [6, 4],
                          pointRadius: 0,
                        },
                      ]
                    : []),
                ],
              }}
              options={{
                maintainAspectRatio: false,
                plugins: {
                  legend: { display: bookings.length > 0 },
                  tooltip: {
                    ...getTooltip(theme),
                    callbacks: {
                      title: ([item]) =>
                        item ? showDate(snapshots[item.parsed.x ?? 0].day) : "",
                      label: ({ parsed }) => currency.format(parsed.y ?? 0),
                    },
                  },
                },
                scales: {
                  x: {
                    type: "linear",
                    min: 0,
                    max: snapshots.length - 1,
                    ticks: {
                      ...getTicks(theme),
                      stepSize: 1,
                      callback: (value) =>
                        snapshots[Number(value)]
                          ? showDate(snapshots[Number(value)].day)
                          : "",
                    },
                  },
                  y: {
                    ticks: {
                      ...getTicks(theme),
                      maxTicksLimit: 5,
                      // in full: the movements are small against the reserve
                      callback: (value) => currency.format(Number(value)),
                    },
                  },
                },
              }}
            />
          </Box>
        )}
      </Paper>

      <Paper variant="outlined" sx={{ ...card, overflow: "hidden" }}>
        <Box sx={{ px: 2.5, pt: 2, pb: 1 }}>
          <SectionLabel component="h3">{t("movements")}</SectionLabel>
        </Box>
        <Box sx={{ overflowX: "auto" }}>
          <Table size="small" sx={{ "& td, & th": { px: 2.5 } }}>
            <TableHead>
              <TableRow>
                <TableCell>{t("column.date")}</TableCell>
                <TableCell>{t("column.what")}</TableCell>
                <TableCell>{t("column.stage")}</TableCell>
                <TableCell align="right">{t("column.indicated")}</TableCell>
                <TableCell align="right">{t("column.movement")}</TableCell>
                <TableCell>{t("column.by")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {snapshots
                .map((snapshot, index) => ({ snapshot, index }))
                .reverse()
                .map(({ snapshot, index }) => (
                  <TableRow key={`${snapshot.at}-${index}`}>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {showDate(snapshot.day)}
                    </TableCell>
                    <TableCell component="th" scope="row">
                      {snapshot.events.map(describe).join(" · ")}
                    </TableCell>
                    <TableCell>
                      {snapshot.stage === undefined
                        ? "–"
                        : stageName(snapshot.stage)}
                    </TableCell>
                    <TableCell align="right" sx={amount}>
                      {currency.format(totals[index])}
                    </TableCell>
                    <TableCell align="right" sx={amount}>
                      {index === 0
                        ? "–"
                        : signed(
                            totals[index] - totals[index - 1],
                            currency.format,
                          )}
                    </TableCell>
                    <TableCell>
                      {snapshot.imported
                        ? snapshot.by
                          ? t("importedBy", { name: snapshot.by.name })
                          : t("imported")
                        : (snapshot.by?.name ?? "–")}
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </Box>
        <Typography
          variant="caption"
          component="p"
          sx={{ px: 2.5, py: 1.5, color: "text.secondary" }}
        >
          {t("note")}
        </Typography>
      </Paper>
    </>
  );
};

/** A movement with its sign: +£1.00, −£1.00, or £0.00. */
const signed = (value: number, format: (value: number) => string) =>
  value > 0 ? `+${format(value)}` : format(value);

/** The reserve booked last: the latest day, and of one day the last booked. */
export function latestBooking(
  bookings: BookedReserve[],
): BookedReserve | undefined {
  return bookings.reduce<BookedReserve | undefined>(
    (latest, booking) =>
      latest === undefined || booking.bookedOn >= latest.bookedOn
        ? booking
        : latest,
    undefined,
  );
}
